import { useCallback, useEffect, useMemo, useRef } from 'react';
import { QueryObserverResult, RefetchOptions, RefetchQueryFilters } from 'react-query';

import { FolderPermissionsDTO, FolderType, useGetFolderPermissions } from '@/api/generated';
import { useLocation } from '@/hooks/entities/useLocation';
import { getErrorStatus } from '@/utils/error';

import { PermissionContextVariables } from './usePermissions';

type UseFolderPermissionsResult = {
  value?: FolderPermissionsDTO;
  error: unknown;
  isLoading: boolean;
  isAccessDenied: boolean;
  refetch: <TPageData>(
    options?: RefetchOptions & RefetchQueryFilters<TPageData>
  ) => Promise<QueryObserverResult<FolderPermissionsDTO, unknown>> | undefined;
};
const ALLOWED_LOCATION_TYPES = [FolderType.FOLDER, FolderType.SUB_FOLDER] as readonly FolderType[];

export const useFolderPermissions = ({
  variables
}: {
  variables?: PermissionContextVariables;
} = {}): UseFolderPermissionsResult => {
  const location = useLocation();

  const organizationSlug = variables?.organizationSlug ?? location.organizationSlug;
  const locationSlug = variables?.locationSlug ?? location.locationSlug;
  const locationType = variables?.locationType ?? location.locationType;

  const hasValidFolderContext =
    !!organizationSlug && !!locationSlug && !!locationType && ALLOWED_LOCATION_TYPES.includes(locationType);

  const {
    data: folderPermissions,
    error,
    isLoading,
    refetch
  } = useGetFolderPermissions(organizationSlug!, locationSlug!, {
    query: { enabled: hasValidFolderContext }
  });

  const isAccessDenied = useMemo(() => [401, 403, 404].includes(getErrorStatus(error) ?? 0), [error]);

  const canReturnValue = hasValidFolderContext && !isAccessDenied;

  const canRefetchValueRef = useRef(canReturnValue);

  useEffect(() => {
    canRefetchValueRef.current = canReturnValue;
  }, [canReturnValue]);

  const refetchWithCanReturnValue = useCallback(
    <TPageData>(options?: RefetchOptions & RefetchQueryFilters<TPageData>) => {
      if (canRefetchValueRef.current) {
        return refetch(options);
      }
    },
    [refetch]
  );

  return {
    value: canReturnValue ? folderPermissions || {} : undefined,
    error,
    isLoading,
    isAccessDenied,
    refetch: refetchWithCanReturnValue
  };
};
